import type { SchemaField } from '@cli-types/core-engine/schema.js'
import { singularize, toPascalCase } from '@cli-utils/text.js'
import { formItemProps } from '@next-adapter/generators/form/form-shared/form-item-props.js'
import { renderListFieldRenderer } from '@next-adapter/templates/renderers/generators/form/field-jsx-nested/render-list-field.js'
import { findTitlePath } from './find-title-path.js'
import { objectValueForFields } from './object-value-for-fields.js'
import { renderNestedField } from './render-nested-field.js'
import { requiredLeafPaths } from './required-leaf-paths.js'
import { validationTriggerExpression } from './validation-trigger-expression.js'

export function renderListField(field: SchemaField, indent: string, label: string): string {
  const listHintJSX = field.hint
    ? `<FormDescription className="text-muted-foreground">${field.hint}</FormDescription>`
    : ''

  if (!field.fields || field.fields.length === 0) {
    const hideLabelProp = field.label ? '' : `\n${indent}  hideLabel`
    const descriptionProp = field.hint
      ? `\n${indent}  description={${JSON.stringify(field.hint)}}`
      : ''
    return renderListFieldRenderer({
      kind: 'renderListFieldDynamicListFieldTemplate',
      props: {
        indent: indent,
        fieldName: field.name,
        label: label,
        hideLabelProp: hideLabelProp,
        descriptionProp: descriptionProp,
        fieldMaxItemsContent: field.maxItems ? `\n${indent}  maxItems={${field.maxItems}}` : ''
      }
    })
  }

  const singularLabel = singularize(label)
  const pascalFieldName = toPascalCase(field.name)

  const titlePath = findTitlePath(field.fields)
  const accordionTitle = titlePath
    ? `{String(form.watch(\`${field.name}.\${index}.${titlePath!.join('.')}\` as never) || \`${singularLabel} \${index + 1}\`)}`
    : `${singularLabel} {index + 1}`

  const nestedFieldsJSX = field.fields
    .map((nf) =>
      renderNestedField(
        nf,
        `${indent}                  `,
        [field.name, { expression: 'index' }, nf.name],
        0
      )
    )
    .join('\n')
  const defaultItem = objectValueForFields(field.fields)
  const validateExpandedItemExpression = validationTriggerExpression(
    field.name,
    requiredLeafPaths(field.fields)
  )

  return renderListFieldRenderer({
    kind: 'renderListFieldCardContentTemplate',
    props: {
      indent: indent,
      formItemProps: formItemProps(field),
      fieldName: field.name,
      toLowerCase: label.toLowerCase(),
      validateExpandedItemExpression: validateExpandedItemExpression,
      defaultItem: defaultItem,
      pascalFieldName: pascalFieldName,
      singularLabel: singularLabel,
      fieldLabelFieldLabel: field.label ? 'text-foreground' : 'sr-only',
      label: label,
      listHintJsxContent: listHintJSX ? `${indent}              ${listHintJSX}\n` : '',
      fieldMaxItemsFieldName: field.maxItems
        ? ` || ${field.name}FieldArray.fields.length >= ${field.maxItems}`
        : '',
      accordionTitle: accordionTitle,
      nestedFieldsJsx: nestedFieldsJSX
    }
  })
}
